/* eslint-disable arrow-body-style, max-len, no-inline-comments, react/jsx-no-bind, react/no-multi-comp, no-empty-function */
import React, {useEffect, useState} from "react";
import API from "../../../shared/modules/API";
import AddToCartRings from "./AddToCartRings";
import CartAPI from "../../../shared/modules/CartAPI";
import ColorOptions from "./ColorOptions";
import LoadingSpinner from "../../BuyItNow/components/LoadingSpinner";
import NonMemberCartAPI from "../../../shared/modules/NonMemberCartAPI";
import PropTypes from "prop-types";
import useComponentVisible from "../../Site/components/useComponentVisible";

const AddToCart = ({
  allRingSizes,
  availableRingSizes,
  defaultProductDescriptorId,
  defaultSwatchId,
  defaultRingSize,
  isRing,
  selectedSwatchId,
  selectedRingSize,
  setHoveredSwatchId,
  setNotificationType,
  setSelectedSwatchId,
  setSelectedRingSize,
  showClearancePrice,
  swatches
}) => {
  const hasManyColorOptions = Boolean(swatches.length > 1);
  const hasManySizeOptions = Boolean(availableRingSizes.length > 1);
  const hasSelections = isRing || hasManyColorOptions;

  const [
    isLoading,
    setIsLoading
  ] = useState(false);

  const [
    selectedProductDescriptorId,
    setSelectedProductDescriptorId
  ] = useState(null);

  const {
    ref,
    "isComponentVisible": showSelections,
    "setIsComponentVisible": setShowSelections
  } = useComponentVisible(false);

  const getButtonTextForRing = () => {
    if (selectedRingSize && selectedSwatchId) return "Confirm Choice";
    if (selectedRingSize && hasManyColorOptions) return "Select Color";
    if (selectedSwatchId && hasManySizeOptions) return "Select Size";
    if (hasManyColorOptions && hasManySizeOptions) return "Select Color + Size";
    return "Add to Cart";
  };

  const getButtonText = () => {
    if (!showSelections) return "Add to Cart";
    if (isRing) return getButtonTextForRing();
    if (selectedSwatchId) return "Confirm Choice";
    if (hasManyColorOptions) return "Select Color";
    return "Add to Cart";
  };

  const getProductDescriptorId = () => {
    if (!selectedSwatchId) return null;

    const matchedSwatch = swatches.find(swatch => swatch.swatch_id === selectedSwatchId);

    if (!isRing) return matchedSwatch.available[0].id;

    if (!selectedRingSize) return null;

    const matchedRingSize = matchedSwatch.available.find(item => item.ring_size === selectedRingSize);

    return matchedRingSize ? matchedRingSize.id : null;
  };

  const handleOnError = () => {
    setNotificationType("itemSoldOut");
  };

  const handleOnSuccess = () => {
    setNotificationType("itemAddedToCart");
    setShowSelections(false);
  };

  const handleAddToCart = () => {
    setIsLoading(true);

    if (!selectedProductDescriptorId) {
      setIsLoading(false);
      return;
    }

    const storeKind = showClearancePrice ? "clearance" : "retail";
    const quantity = 1;

    if (API.CUSTOMER_ID) {
      CartAPI.addToCart(selectedProductDescriptorId, quantity, storeKind, () => {}).then((response) => {
        if (response.error) {
          handleOnError();
        } else {
          handleOnSuccess();
        }
      });
    } else {
      NonMemberCartAPI.addToCart(selectedProductDescriptorId, quantity, storeKind).then((response) => {
        if (response.error) {
          handleOnError();
        } else {
          handleOnSuccess();
        }
      });
    }

    setIsLoading(false);
  };

  const toggleSelections = () => {
    setShowSelections(prevState => !prevState);
  };

  const handleOnClickContainer = (event) => {
    if (event.target === ref.current) {
      setShowSelections(false);
    }
  };

  const filterSizesBySelectedColor = () => {
    if (!selectedSwatchId) return availableRingSizes;

    const filteredSwatches = swatches.filter(swatch => swatch.swatch_id === selectedSwatchId);

    const [filteredSizes] = filteredSwatches.map(swatch => swatch.available.map(item => item.ring_size));

    if (selectedRingSize && !filteredSizes.includes(selectedRingSize)) setSelectedRingSize(null);

    return filteredSizes;
  };

  useEffect(() => {
    setSelectedProductDescriptorId(defaultProductDescriptorId);
  }, []);

  useEffect(() => {
    if (!showSelections) {
      setSelectedSwatchId(defaultSwatchId);
      setSelectedRingSize(defaultRingSize);
      setSelectedProductDescriptorId(defaultProductDescriptorId);
    }
  }, [showSelections]);

  useEffect(() => {
    setSelectedProductDescriptorId(getProductDescriptorId());
  }, [
    selectedSwatchId,
    selectedRingSize
  ]);

  return (
    <div
      className="flex-1 flex flex-col justify-end"
      data-test="add-to-cart-from-grid"
    >
      {showSelections &&
      <div
        className="selections-container flex-1 flex flex-col"
        onClick={handleOnClickContainer}
        ref={ref}
      >
        <button
          className="m-1.5 right-0 top-0 absolute z-1 size-8 rounded-full hover:bg-neutral-100 focus:bg-neutral-100 active:bg-neutral-100 flex justify-center items-center"
          data-test="add-to-cart-from-grid-close"
          onClick={toggleSelections}
          type="button"
        >
          <i className="fa-light fa-xmark" />
        </button>
        <div className="selections-sections flex-1 flex flex-col">
          {(isRing || hasManyColorOptions) &&
          <ColorOptions
            selectedSwatchId={selectedSwatchId}
            setHoveredSwatchId={setHoveredSwatchId}
            setSelectedSwatchId={setSelectedSwatchId}
            swatches={swatches}
          />}
          {isRing &&
          <AddToCartRings
            allRingSizes={allRingSizes}
            availableRingSizes={filterSizesBySelectedColor()}
            selectedRingSize={selectedRingSize}
            setSelectedRingSize={setSelectedRingSize}
          />}
        </div>
        <div className="py-2 px-2">
          <button
            className={`${isLoading ? "button-dark" : "button-outlined-white"} button-sm w-full`}
            data-test="add-to-cart-from-grid-selection-button"
            disabled={isLoading}
            onClick={getButtonText().startsWith("Select") ? null : handleAddToCart}
            type="button"
          >
            <span className="uppercase tracking-wider block pt-[2px]">
              {isLoading &&
              <LoadingSpinner size={21} />}
              {!isLoading &&
              getButtonText()}
            </span>
          </button>
        </div>
      </div>}
      <div className="pt-2 px-2">
        <button
          className={`${isLoading ? "button-dark" : "button-outlined-white"} button-sm w-full`}
          data-test="add-to-cart-from-grid-button"
          disabled={isLoading}
          onClick={hasSelections ? toggleSelections : handleAddToCart}
          type="button"
        >
          {isLoading &&
          <LoadingSpinner size={21} />}
          {!isLoading &&
          <span className="uppercase tracking-wider block pt-[2px]">
            Add to Cart
          </span>}
        </button>
      </div>
    </div>
  );
};

AddToCart.propTypes = {
  "allRingSizes": PropTypes.array.isRequired,
  "availableRingSizes": PropTypes.array,
  "defaultProductDescriptorId": PropTypes.number,
  "defaultRingSize": PropTypes.string,
  "defaultSwatchId": PropTypes.number,
  "isRing": PropTypes.bool.isRequired,
  "selectedRingSize": PropTypes.string,
  "selectedSwatchId": PropTypes.number,
  "setHoveredSwatchId": PropTypes.func.isRequired,
  "setNotificationType": PropTypes.func.isRequired,
  "setSelectedRingSize": PropTypes.func.isRequired,
  "setSelectedSwatchId": PropTypes.func.isRequired,
  "showClearancePrice": PropTypes.bool.isRequired,
  "swatches": PropTypes.array.isRequired
};

AddToCart.defaultProps = {
  "availableRingSizes": [],
  "defaultProductDescriptorId": null,
  "defaultRingSize": null,
  "defaultSwatchId": null,
  "selectedRingSize": null,
  "selectedSwatchId": null
};

export default AddToCart;
